import React from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Grid, Box, Typography, Tooltip } from '@material-ui/core';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import TrendingDownIcon from '@material-ui/icons/TrendingDown';
import { i18n } from "../../translate/i18n";

import SupervisedUserCircleIcon from '@material-ui/icons/SupervisedUserCircle';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import BarChartIcon from '@material-ui/icons/BarChart';
import CallIcon from '@material-ui/icons/Call';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import MessageIcon from '@material-ui/icons/Message';
import SendIcon from '@material-ui/icons/Send';
import AccessAlarmIcon from '@material-ui/icons/AccessAlarm';
import TimerIcon from '@material-ui/icons/Timer';

const useStyles = makeStyles((theme) => ({
  miniCard: {
    display: "flex",
    flexDirection: "column",
    borderRadius: theme.borderRadius?.medium || 12,
    overflow: "hidden",
    boxShadow: theme.palette.shadow?.light || theme.shadows[1],
    height: "100%",
    transition: "transform 0.2s ease-in-out",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: theme.palette.shadow?.medium || theme.shadows[3],
    }
  },
  miniCardHeader: {
    padding: theme.spacing(0.75, 1),
    color: "#FFF",
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    fontSize: "0.85rem",
  },
  miniCardContent: {
    padding: theme.spacing(0.75, 1),
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  miniCardValue: {
    fontSize: "1.4rem",
    fontWeight: 600,
    lineHeight: 1.2,
  },
  miniCardSubtext: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    textAlign: "center",
  },
  positiveChange: {
    color: theme.palette.success.main,
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  negativeChange: {
    color: theme.palette.error.main,
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  progressCircle: {
    width: 40,
    height: 40,
    position: "absolute",
    top: 6,
    right: 6,
  },
  progressContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  progressValue: {
    fontSize: '0.7rem',
    fontWeight: 700,
  },
  icon: {
    fontSize: '0.8rem',
    marginRight: theme.spacing(0.5),
  },
  headerIcon: {
    fontSize: '1rem',
  },
  clickableCard: {
    cursor: 'pointer',
    transition: 'all 0.2s',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: theme.palette.shadow?.medium || theme.shadows[3],
    },
    '&:active': {
      transform: 'translateY(-1px)',
    }
  },
}));

const formatTime = (minutes) => {
  if (!minutes || isNaN(minutes)) return "0m";
  const mins = Math.round(minutes);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  return remainingMins > 0 ? `${hours}h ${remainingMins}m` : `${hours}h`;
};

const MetricCards = ({ counters, extendedMetrics, GetUsers, attendantsList, messagesReceived, messagesSent }) => {
  const theme = useTheme();
  const classes = useStyles();

  return (
    <Grid container spacing={2}>
      {/* Tickets Ativos */}
      <Grid item xs={6} sm={4} md={3} lg={2}>
        <Box className={classes.miniCard}>
          <Box className={classes.miniCardHeader} style={{ backgroundColor: theme.palette.warning.main }}>
            <Typography variant="body2">{i18n.t("dashboard.cards.activeTickets") || "Tickets Ativos"}</Typography>
            <TrendingUpIcon className={classes.headerIcon} />
          </Box>
          <Box className={classes.miniCardContent}>
            <Typography className={classes.miniCardValue}>
              {counters?.activeTickets || 0}
            </Typography>
            <Tooltip title={i18n.t("dashboard.tickets.activeTicketsTooltip") || "Tickets que foram iniciados pelo cliente"} arrow>
              <Typography variant="caption" className={classes.miniCardSubtext}>
                {i18n.t("dashboard.tickets.demandActive") || "Demanda ativa"}
              </Typography>
            </Tooltip>
          </Box>
        </Box>
      </Grid>

      {/* Tickets Passivos */}
      <Grid item xs={6} sm={4} md={3} lg={2}>
        <Box className={classes.miniCard}>
          <Box className={classes.miniCardHeader} style={{ backgroundColor: theme.palette.secondary.dark }}>
            <Typography variant="body2">{i18n.t("dashboard.cards.passiveTickets") || "Tickets Passivos"}</Typography>
            <TrendingDownIcon className={classes.headerIcon} />
          </Box>
          <Box className={classes.miniCardContent}>
            <Typography className={classes.miniCardValue}>
              {counters?.passiveTickets || 0}
            </Typography>
            <Tooltip title={i18n.t("dashboard.tickets.passiveTicketsTooltip") || "Tickets que foram iniciados pela empresa"} arrow>
              <Typography variant="caption" className={classes.miniCardSubtext}>
                {i18n.t("dashboard.tickets.demandPassive") || "Demanda passiva"}
              </Typography>
            </Tooltip>
          </Box>
        </Box>
      </Grid>

      {/* Em Atendimento */}
      <Grid item xs={6} sm={4} md={3} lg={2}>
        <Box className={classes.miniCard}>
          <Box className={classes.miniCardHeader} style={{ backgroundColor: "#01BBAC" }}>
            <Typography variant="body2">{i18n.t("dashboard.cards.inAttendance") || "Em Atendimento"}</Typography>
            <CallIcon className={classes.headerIcon} />
          </Box>
          <Box className={classes.miniCardContent}>
            <Typography className={classes.miniCardValue}>
              {counters?.supportHappening || 0}
            </Typography>
            <Tooltip title={i18n.t("dashboard.tickets.inAttendanceTooltip") || "Tickets sendo atendidos agora"} arrow>
              <Typography variant="caption" className={classes.miniCardSubtext}>
                Atendimentos ativos
              </Typography>
            </Tooltip>
          </Box>
        </Box>
      </Grid>

      {/* Aguardando */}
      <Grid item xs={6} sm={4} md={3} lg={2}>
        <Box className={classes.miniCard}>
          <Box className={classes.miniCardHeader} style={{ backgroundColor: "#47606e" }}>
            <Typography variant="body2">{i18n.t("dashboard.cards.waiting") || "Aguardando"}</Typography>
            <HourglassEmptyIcon className={classes.headerIcon} />
          </Box>
          <Box className={classes.miniCardContent}>
            <Typography className={classes.miniCardValue}>
              {counters?.supportPending || 0}
            </Typography>
            <Tooltip title={i18n.t("dashboard.tickets.waitingTooltip") || "Tickets aguardando atendimento"} arrow>
              <Typography variant="caption" className={classes.miniCardSubtext}>
                Na fila
              </Typography>
            </Tooltip>
          </Box>
        </Box>
      </Grid>

      {/* Finalizados */}
      <Grid item xs={6} sm={4} md={3} lg={2}>
        <Box className={classes.miniCard}>
          <Box className={classes.miniCardHeader} style={{ backgroundColor: "#5852ab" }}>
            <Typography variant="body2">{i18n.t("dashboard.cards.finalized") || "Finalizados"}</Typography>
            <CheckCircleIcon className={classes.headerIcon} />
          </Box>
          <Box className={classes.miniCardContent}>
            <Typography className={classes.miniCardValue}>
              {counters?.supportFinished || 0}
            </Typography>
            <Tooltip title={i18n.t("dashboard.tickets.finalizedTooltip") || "Tickets fechados no período"} arrow>
              <Typography variant="caption" className={classes.miniCardSubtext}>
                No período
              </Typography>
            </Tooltip>
          </Box>
        </Box>
      </Grid>

      {/* Mensagens Recebidas */}
      <Grid item xs={6} sm={4} md={3} lg={2}>
        <Box className={classes.miniCard}>
          <Box className={classes.miniCardHeader} style={{ backgroundColor: "#333133" }}>
            <Typography variant="body2">{i18n.t("dashboard.cards.totalReceivedMessages") || "Msg. Recebidas"}</Typography>
            <MessageIcon className={classes.headerIcon} />
          </Box>
          <Box className={classes.miniCardContent}>
            <Typography className={classes.miniCardValue}>
              {messagesReceived || 0}
            </Typography>
            <Tooltip title="Total de mensagens recebidas no período" arrow>
              <Typography variant="caption" className={classes.miniCardSubtext}>
                No período
              </Typography>
            </Tooltip>
          </Box>
        </Box>
      </Grid>

      {/* Mensagens Enviadas */}
      <Grid item xs={6} sm={4} md={3} lg={2}>
        <Box className={classes.miniCard}>
          <Box className={classes.miniCardHeader} style={{ backgroundColor: "#558a59" }}>
            <Typography variant="body2">{i18n.t("dashboard.cards.totalSentMessages") || "Msg. Enviadas"}</Typography>
            <SendIcon className={classes.headerIcon} />
          </Box>
          <Box className={classes.miniCardContent}>
            <Typography className={classes.miniCardValue}>
              {messagesSent || 0}
            </Typography>
            <Tooltip title="Total de mensagens enviadas no período" arrow>
              <Typography variant="caption" className={classes.miniCardSubtext}>
                No período
              </Typography>
            </Tooltip>
          </Box>
        </Box>
      </Grid>

      {/* T.M. de Atendimento */}
      <Grid item xs={6} sm={4} md={3} lg={2}>
        <Box className={classes.miniCard}>
          <Box className={classes.miniCardHeader} style={{ backgroundColor: "#F79009" }}>
            <Typography variant="body2">{i18n.t("dashboard.cards.averageServiceTime") || "T.M. Atendimento"}</Typography>
            <AccessAlarmIcon className={classes.headerIcon} />
          </Box>
          <Box className={classes.miniCardContent}>
            <Typography className={classes.miniCardValue}>
              {formatTime(counters?.avgSupportTime)}
            </Typography>
            <Tooltip title="Tempo médio de atendimento" arrow>
              <Typography variant="caption" className={classes.miniCardSubtext}>
                Tempo médio
              </Typography>
            </Tooltip>
          </Box>
        </Box>
      </Grid>

      {/* T.M. de Espera */}
      <Grid item xs={6} sm={4} md={3} lg={2}>
        <Box className={classes.miniCard}>
          <Box className={classes.miniCardHeader} style={{ backgroundColor: "#8a2c40" }}>
            <Typography variant="body2">{i18n.t("dashboard.cards.averageWaitingTime") || "T.M. Espera"}</Typography>
            <TimerIcon className={classes.headerIcon} />
          </Box>
          <Box className={classes.miniCardContent}>
            <Typography className={classes.miniCardValue}>
              {formatTime(counters?.avgWaitTime)}
            </Typography>
            <Tooltip title="Tempo médio de espera na fila" arrow>
              <Typography variant="caption" className={classes.miniCardSubtext}>
                Tempo médio
              </Typography>
            </Tooltip>
          </Box>
        </Box>
      </Grid>

      {/* Média de Tickets por Dia */}
      <Grid item xs={6} sm={4} md={3} lg={2}>
        <Box className={classes.miniCard}>
          <Box className={classes.miniCardHeader} style={{ backgroundColor: theme.palette.primary.dark }}>
            <Typography variant="body2">Tickets por Dia</Typography>
            <BarChartIcon className={classes.headerIcon} />
          </Box>
          <Box className={classes.miniCardContent}>
            <Typography className={classes.miniCardValue}>
              {Math.round(extendedMetrics?.avgTicketsPerDay || 0)}
            </Typography>
            <Tooltip title={i18n.t("dashboard.avgTicketsPerDayTooltip") || "Média diária de tickets no período"} arrow>
              <Typography variant="caption" className={classes.miniCardSubtext}>
                Média diária
              </Typography>
            </Tooltip>
          </Box>
        </Box>
      </Grid>

      {/* Novos Contatos */}
      <Grid item xs={6} sm={4} md={3} lg={2}>
        <Box className={classes.miniCard}>
          <Box className={classes.miniCardHeader} style={{ backgroundColor: theme.palette.info.dark }}>
            <Typography variant="body2">{i18n.t("dashboard.cards.newContacts") || "Novos Contatos"}</Typography>
            <PersonAddIcon className={classes.headerIcon} />
          </Box>
          <Box className={classes.miniCardContent}>
            <Typography className={classes.miniCardValue}>
              {counters?.leads || 0}
            </Typography>
            <Tooltip title={i18n.t("dashboard.newContactsTooltip") || "Novos contatos registrados no período selecionado"} arrow>
              <Typography variant="caption" className={classes.miniCardSubtext}>
                {i18n.t("dashboard.newContacts") || "No período"}
              </Typography>
            </Tooltip>
          </Box>
        </Box>
      </Grid>

      {/* Atendentes Ativos */}
      <Grid item xs={6} sm={4} md={3} lg={2}>
        <Box className={classes.miniCard}>
          <Box className={classes.miniCardHeader} style={{ backgroundColor: theme.palette.success.dark }}>
            <Typography variant="body2">{i18n.t("dashboard.cards.activeAttendants") || "Atendentes"}</Typography>
            <SupervisedUserCircleIcon className={classes.headerIcon} />
          </Box>
          <Box className={classes.miniCardContent}>
            <Typography className={classes.miniCardValue}>
              {`${GetUsers()}/${Array.isArray(attendantsList) ? attendantsList.length : 0}`}
            </Typography>
            <Tooltip title={i18n.t("dashboard.activeAttendantsTooltip") || "Número de atendentes disponíveis online"} arrow>
              <Typography variant="caption" className={classes.miniCardSubtext}>
                {i18n.t("dashboard.online") || "Online agora"}
              </Typography>
            </Tooltip>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default React.memo(MetricCards); 